import { isEditableEventTarget } from '@littlebird/action-cards';

export type FeedShortcutActions = {
  toggle?: () => void;
  // Puts the caret in the object's first field; only an open card has one
  edit?: () => void;
  chat?: () => void;
  dismiss?: () => void;
  // Checks the card off: marks a to-do or a draft done, or an update read
  markDone?: () => void;
  runPrimary?: () => void;
  // Only the collapsed row has these, beside its hover buttons
  pin?: () => void;
  snooze?: () => void;
};

type ShortcutEvent = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'target'
> & { preventDefault: () => void; stopPropagation: () => void };

const actionFor = (
  event: ShortcutEvent,
  actions: FeedShortcutActions,
  card: Element | null | undefined
): (() => void) | undefined => {
  const key = event.key.toLowerCase();
  // Every action but opening needs ⌘ (Ctrl off macOS), so a stray letter never acts on the card
  if (event.metaKey || event.ctrlKey) {
    if (event.shiftKey) {
      if (key === 'p') return actions.pin;
      if (key === 's') return actions.snooze;
      return undefined;
    }
    if (key === 'enter') return actions.runPrimary;
    if (key === 'e') return actions.edit;
    if (key === 'j') return actions.chat;
    if (key === 'backspace' || key === 'delete') return actions.dismiss;
    if (key === 'd') return actions.markDone;
    return undefined;
  }
  if (event.shiftKey) return undefined;
  // A focused button or link in the card handles its own Enter and Space; one elsewhere on the page doesn't
  const ownsActivation =
    event.target instanceof Element &&
    event.target.closest('button, a') !== null &&
    (!card || card.contains(event.target));
  if ((key === 'enter' || key === ' ') && !ownsActivation)
    return actions.toggle;
  return undefined;
};

// Enter, ⌘E, ⌘J, ⌘⌫, ⌘D and ⌘↵ on the focused or open feed row, and ⌘⇧P and ⌘⇧S on the focused row; typing in a
// field or a menu keeps its keys. `card` is the open card, for keys pressed anywhere on the page
export function handleFeedShortcut(
  event: ShortcutEvent,
  actions: FeedShortcutActions,
  card?: Element | null
): boolean {
  if (event.altKey) return false;
  if (isEditableEventTarget(event.target)) return false;
  if (
    event.target instanceof Element &&
    event.target.closest(
      '[role="dialog"], [role="menu"], [role="listbox"], [data-feed-own-keys]'
    )
  )
    return false;
  const action = actionFor(event, actions, card);
  if (!action) return false;
  event.preventDefault();
  // ⌘J opens the app's context chat anywhere else, so a card's ⌘ keys stop with the card. Enter and Space still
  // reach the element that has focus
  if (event.metaKey || event.ctrlKey) event.stopPropagation();
  action();
  return true;
}

// A title at rest is a button that becomes its field once focused
const FIRST_FIELD =
  'textarea:not([disabled]), input:not([type="hidden"]):not([type="checkbox"]):not([disabled]), [contenteditable="true"], [data-field-at-rest]';

export const focusFirstField = (frame: HTMLElement | null): void => {
  frame?.querySelector<HTMLElement>(FIRST_FIELD)?.focus();
};
